import {Injectable} from '@angular/core';
import {WindowService, ApiService, merge, SessionState} from '@myvf/core';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {Store} from '@ngxs/store';
import CryptoAES from 'crypto-js/aes';

@Injectable({
  providedIn: 'root'
})
export class AnchorService {
  constructor(private api: ApiService, private win: WindowService, private store: Store) {
  }

  /**
   * Sanitize pbo param into android OS url scheme
   * @param anchor client's url scheme
   * @param pbo param to be sanitized
   */
  androidAnchorSanitized(anchor: string, pbo = ''): string {
    if (/\/native/.test(anchor)) {
      return anchor;
    }
    const domain = anchor.split('?')[0];
    const anchorParams = this.win.decodeQueryParams(anchor);
    const pageUrl = this.win.decode(anchorParams['pageURL']);
    const pageUrlParams = this.win.encodeQueryParams({...this.win.decodeQueryParams(pageUrl), ...(pbo && {pbo})});
    const encodedPageUrl = `${this.win.encode(pageUrl.split('?')[0])}?${this.win.encode(
      this.win.encode(pageUrlParams)
    )}`;
    const sanitizedParams = {...anchorParams, pageURL: encodedPageUrl};
    return `${domain}?${this.win.encodeQueryParams(sanitizedParams)}`;
  }

  /**
   * Get the OneCLick anchor
   * @param sessionData session state data
   * @return Observable<any>
   */
  getRechargeOneClickAnchor(sessionData): Observable<any> {
    const {value, type_sim, status} = sessionData;
    let anchorId: string;
    let args = null;

    if (type_sim === 'ricaricabile' && status !== 'anonymous') {
      args = {msisdn: value};
      anchorId = 'NA_RICARICA_WITH_TOKEN_NO_OV';
    } else {
      anchorId = 'NA_RICARICA_ANONYMOUS_NO_OV';
    }

    return this.lookUp(anchorId, args);
  }

  getExternalAnchor(anchorId: string, args: any): Observable<any> {
    const {type, value} = this.store.selectSnapshot(SessionState);

    args = {
      ...args,
      ...(type === 'sim' && {msisdn: value}),
      ...(type === 'landline' && {landline: value})
    };

    return this.lookUp(anchorId, args);
  }

  /**
   * api service for anchors resolve
   * @param anchorID, id of anchor
   * @param args, optional if anchor must have args
   * @return Observable<any>
   */
  lookUp(anchorId: string, args?: any): Observable<any> {
    return this.api.anchors.getLookup(anchorId).pipe(
      map((data) => {
        let { actionUrl } = data;

        if (this.win.isAndroid() && typeof actionUrl !== 'undefined') {
          const {
            auth: {pbo}
          } = this.store.selectSnapshot(SessionState);

          actionUrl = this.androidAnchorSanitized(actionUrl, pbo);
          data = merge([data, {result: {actionUrl}}]);
        }

        return data;
      })
    );
  }

  /**
   * create anchor in FE
   * @param pageUrl url of anchor (not encoded)
   * @param hybrid true if hybrid false otherwise
   * @param reuse anchor param for client
   * @param relaunch anchor param for client
   * @param spa anchor param for client
   */
  makeAnchor(pageUrl: string, hybrid = true, reuse = false, relaunch = false, spa = false): string {
    const schemaBaseUrl = `myvfitbiz://view/${
      hybrid ? 'hybrid' : 'native'
    }?reuse=${reuse}&relaunch=${relaunch}&spa=${spa}`;
    return `${schemaBaseUrl}&pageURL=${this.win.encode(pageUrl)}`;
  }

  /**
   * retrieve original anchor with page url query params added
   * @param anchor src anchor
   * @param queryParams object with key-value of query params to add
   */
  addAnchorPageUrlParams(anchor: string, queryParams: {[key: string]: string}) {
    const baseAnchor = anchor.split('?')[0];
    const anchorParams = this.win.decodeQueryParams(anchor);
    const pageUrl = this.win.decode(anchorParams['pageURL']);
    const pageUrlParams = this.win.encodeQueryParams({...this.win.decodeQueryParams(pageUrl), ...queryParams});
    const encodedPageUrl = this.win.encode(`${pageUrl.split('?')[0]}?${pageUrlParams}`);
    const retAnchorParams = {...anchorParams, pageURL: encodedPageUrl};

    return `${baseAnchor}?${this.win.encodeQueryParams(retAnchorParams)}`;
  }

  addMsisdn(anchor: string, msisdn: string) {
    return this.addAnchorPageUrlParams(anchor, {msisdn: msisdn});
  }
}
